import {
  Checkbox,
  Divider,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type { ReactNode } from "react";
import { AvailabilityChips } from "./AvailabilityChips";
import { CityMultiSelect } from "./CityMultiSelect";
import { CtcLakhsInput } from "./CtcLakhsInput";
import { ExperienceFields } from "./ExperienceFields";
import { LanguageTagsField } from "./LanguageTagsField";
import { renderSegmentOption } from "./SegmentOptionLabel";
import { SkillTagsField } from "./SkillTagsField";
import { AiFilledField } from "./ui";
import {
  GRADUATION_YEAR_MAX_OFFSET,
  GRADUATION_YEAR_MIN,
  educationSummaryFromForm,
  type CandidateFormValues,
} from "../lib/candidateForm";
import {
  DEGREE_OPTIONS,
  EDUCATION_LEVEL_OPTIONS,
  degreeLabel,
  educationLevelLabel,
  isGraduateLevel,
} from "../lib/educationCatalog";
import { ROLE_FAMILY_LABEL, ROLE_FAMILY_PLACEHOLDER } from "../constants/matching";
import { catalogFuzzyFilter } from "../lib/catalogFuzzyFilter";
import {
  ENGLISH_LEVEL_OPTIONS,
  GENDER_LABELS,
  GENDER_OPTIONS,
  WORK_STATUS_OPTIONS,
  type Gender,
} from "../lib/formCatalogs";

const APPLY_FIELD_COLUMNS = { base: 1, sm: 2 } as const;
const PROFILE_SECTION_GAP = "var(--axio-unit-40)";
const PROFILE_FIELD_GAP = "var(--axio-unit-24)";
const PROFILE_CONTROL_GAP = "var(--axio-unit-16)";
const PROFILE_HEADING_GAP = "var(--axio-unit-6)";

type ProfileSectionProps = {
  title: string;
  description: string;
  children: ReactNode;
  showHeading?: boolean;
  divided?: boolean;
};

function ProfileSection({
  title,
  description,
  children,
  showHeading = true,
  divided = false,
}: ProfileSectionProps) {
  return (
    <Stack component="section" gap={PROFILE_FIELD_GAP}>
      {divided && <Divider />}
      {showHeading && (
        <Stack gap={PROFILE_HEADING_GAP}>
          <Title order={3} size="h4">{title}</Title>
          <Text size="sm" c="dimmed">{description}</Text>
        </Stack>
      )}
      {children}
    </Stack>
  );
}

export type CandidateProfileSection = "all" | "personal" | "professional";

type Props<T extends CandidateFormValues> = {
  form: UseFormReturnType<T>;
  parsedFields: Set<string>;
  disabled?: boolean;
  publicCatalog?: boolean;
  skillSuggestions: string[];
  languageSuggestions: string[];
  segmentData: { value: string; label: string }[];
  segmentLabel: string;
  segmentDescription: string;
  section?: CandidateProfileSection;
  ctcRequired?: boolean;
  showWhatsapp?: boolean;
  aboutLabel?: string;
  namePlaceholder?: string;
  citiesPlaceholder?: string;
  phoneField?: ReactNode;
  workStatusData?: { label: string; value: string }[];
  segmentsRequired?: boolean;
  roleFamilyData?: { value: string; label: string }[];
};

export function CandidateProfileFields<T extends CandidateFormValues>({
  form,
  parsedFields,
  disabled,
  publicCatalog,
  skillSuggestions,
  languageSuggestions,
  segmentData,
  segmentLabel,
  segmentDescription,
  section = "all",
  ctcRequired = true,
  showWhatsapp = true,
  aboutLabel = "About you",
  namePlaceholder = "As on your resume / ID",
  citiesPlaceholder = "Where you can work",
  phoneField,
  workStatusData,
  segmentsRequired = true,
  roleFamilyData,
}: Props<T>) {
  const showGaps = parsedFields.size > 0;
  const filled = (key: string) => parsedFields.has(key);
  const v = form.values;
  const showPersonal = section !== "professional";
  const showProfessional = section !== "personal";
  const setCandidateField = <K extends keyof CandidateFormValues>(
    key: K,
    value: CandidateFormValues[K],
  ) => form.setValues((current) => ({ ...current, [key]: value }));

  return (
    <Stack gap={PROFILE_SECTION_GAP}>
      {showPersonal && (
        <ProfileSection
          title={aboutLabel}
          description="Your contact details and preferred work locations."
          showHeading={section === "all"}
        >
          <SimpleGrid cols={APPLY_FIELD_COLUMNS} spacing={PROFILE_CONTROL_GAP}>
            <AiFilledField aiFilled={filled("full_name")} showGap={showGaps}>
              <TextInput
                label="Full name"
                placeholder={namePlaceholder}
                required
                disabled={disabled}
                size="md"
                {...form.getInputProps("full_name")}
              />
            </AiFilledField>
            {phoneField ? (
              <AiFilledField aiFilled={filled("phone")} showGap={showGaps}>
                {phoneField}
              </AiFilledField>
            ) : null}
            <AiFilledField aiFilled={filled("email")} showGap={showGaps}>
              <TextInput
                label="Email"
                placeholder="you@example.com"
                type="email"
                disabled={disabled}
                size="md"
                {...form.getInputProps("email")}
              />
            </AiFilledField>
            <AiFilledField aiFilled={filled("gender")} showGap={showGaps}>
              <Select
                label="Gender"
                placeholder="Select gender"
                value={v.gender}
                onChange={(next) => setCandidateField("gender", next)}
                data={[...GENDER_OPTIONS]}
                disabled={disabled}
                clearable
                size="md"
              />
            </AiFilledField>
            <AiFilledField aiFilled={filled("cities")} showGap={showGaps}>
              <CityMultiSelect
                label="Preferred cities"
                placeholder={citiesPlaceholder}
                disabled={disabled}
                size="md"
                required
                publicCatalog={publicCatalog}
                value={v.cities}
                onChange={(cities) => setCandidateField("cities", cities)}
                error={form.errors.cities}
              />
            </AiFilledField>
          </SimpleGrid>
          {showWhatsapp && (
            <Checkbox
              label="Send me job alerts and updates on WhatsApp"
              disabled={disabled}
              {...form.getInputProps("whatsapp_opt_in", { type: "checkbox" })}
            />
          )}
        </ProfileSection>
      )}

      {showProfessional && (
        <>
          <ProfileSection
            title="Work & compensation"
            description="Tell us where you are in your career and what you are looking for next."
            divided={showPersonal}
          >
            <SimpleGrid cols={APPLY_FIELD_COLUMNS} spacing={PROFILE_CONTROL_GAP}>
              <AiFilledField aiFilled={filled("current_title")} showGap={showGaps}>
                <TextInput
                  label="Current or latest job title"
                  placeholder="e.g. Customer Support Executive"
                  disabled={disabled}
                  size="md"
                  {...form.getInputProps("current_title")}
                />
              </AiFilledField>
              <Select
                label="Work status"
                placeholder="Select work status"
                value={v.work_status}
                onChange={(next) => {
                  if (!next) return;
                  setCandidateField("work_status", next as CandidateFormValues["work_status"]);
                  if (next === "fresher") setCandidateField("years_experience", 0);
                }}
                data={workStatusData ?? [...WORK_STATUS_OPTIONS]}
                disabled={disabled}
                allowDeselect={false}
                size="md"
              />
            </SimpleGrid>
            {v.work_status === "working" && (
              <ExperienceFields
                value={v.years_experience}
                onChange={(years) => setCandidateField("years_experience", years)}
                disabled={disabled}
                labelYears="Experience in years"
                labelMonths="Additional months"
              />
            )}
            <AvailabilityChips
              value={v.availability_in_days}
              onChange={(days) => setCandidateField("availability_in_days", days)}
              disabled={disabled}
            />
            <SimpleGrid cols={APPLY_FIELD_COLUMNS} spacing={PROFILE_CONTROL_GAP}>
              <CtcLakhsInput
                label="Current CTC (lakhs / year)"
                required={ctcRequired}
                disabled={disabled}
                size="md"
                {...form.getInputProps("current_ctc_lakhs")}
              />
              <CtcLakhsInput
                label="Expected CTC (lakhs / year)"
                required={ctcRequired}
                disabled={disabled}
                size="md"
                {...form.getInputProps("expected_ctc_lakhs")}
              />
            </SimpleGrid>
            <TextInput
              label="LinkedIn profile"
              placeholder="https://linkedin.com/in/..."
              disabled={disabled}
              size="md"
              {...form.getInputProps("linkedin_url")}
            />
          </ProfileSection>

          <ProfileSection
            title="Education"
            description="Add your highest qualification and study details."
            divided
          >
            <AiFilledField aiFilled={filled("education_level")} showGap={showGaps}>
              <Select
                label="Highest education level"
                placeholder="Select education level"
                value={v.education_level}
                onChange={(next) => setCandidateField("education_level", next)}
                data={[...EDUCATION_LEVEL_OPTIONS]}
                disabled={disabled}
                clearable
                size="md"
              />
            </AiFilledField>
            {isGraduateLevel(v.education_level) && (
              <SimpleGrid cols={APPLY_FIELD_COLUMNS} spacing={PROFILE_CONTROL_GAP}>
                <TextInput
                  label="College / University"
                  placeholder="e.g. Delhi University"
                  disabled={disabled}
                  size="md"
                  {...form.getInputProps("college_name")}
                />
                <Select
                  label="Degree"
                  placeholder="Select degree"
                  data={[...DEGREE_OPTIONS]}
                  disabled={disabled}
                  size="md"
                  {...form.getInputProps("degree")}
                />
                <TextInput
                  label="Specialization"
                  placeholder="e.g. Computer Science (optional)"
                  disabled={disabled}
                  size="md"
                  {...form.getInputProps("specialization")}
                />
                <NumberInput
                  label="Completion year"
                  placeholder="e.g. 2022"
                  min={GRADUATION_YEAR_MIN}
                  max={new Date().getFullYear() + GRADUATION_YEAR_MAX_OFFSET}
                  disabled={disabled}
                  size="md"
                  {...form.getInputProps("graduation_year")}
                />
              </SimpleGrid>
            )}
          </ProfileSection>

          <ProfileSection
            title="Roles & skills"
            description="Choose the work you want and the skills you can confidently use."
            divided
          >
            <MultiSelect
              label={segmentLabel}
              description={segmentDescription}
              withAsterisk={segmentsRequired}
              placeholder="Search roles…"
              searchable
              clearable
              disabled={disabled}
              size="md"
              data={segmentData}
              renderOption={renderSegmentOption}
              filter={catalogFuzzyFilter}
              limit={40}
              nothingFoundMessage="No matching roles"
              maxValues={12}
              {...form.getInputProps("segments")}
            />
            {roleFamilyData && roleFamilyData.length > 0 ? (
              <Select
                label={ROLE_FAMILY_LABEL}
                placeholder={ROLE_FAMILY_PLACEHOLDER}
                searchable
                clearable
                disabled={disabled}
                size="md"
                data={roleFamilyData}
                value={v.role_family_code}
                onChange={(next) => setCandidateField("role_family_code", next)}
              />
            ) : null}
            <AiFilledField aiFilled={filled("skills")} showGap={showGaps}>
              <SkillTagsField
                label="Skills"
                required
                description="Search the catalog or type a custom skill and press comma."
                placeholder="e.g. Excel, Zendesk, or type custom…"
                disabled={disabled}
                size="md"
                suggestions={skillSuggestions}
                maxTags={40}
                value={v.skills}
                onChange={(skills) => setCandidateField("skills", skills)}
                error={form.errors.skills}
              />
            </AiFilledField>
          </ProfileSection>

          <ProfileSection
            title="Languages"
            description="Add the languages you speak and your English comfort level."
            divided
          >
            <SimpleGrid cols={APPLY_FIELD_COLUMNS} spacing={PROFILE_CONTROL_GAP}>
              <LanguageTagsField
                disabled={disabled}
                size="md"
                suggestions={languageSuggestions}
                value={v.languages}
                onChange={(languages) => setCandidateField("languages", languages)}
              />
              <Select
                label="English proficiency"
                description="Optional"
                placeholder="Select proficiency"
                value={v.english_level}
                onChange={(next) => setCandidateField("english_level", next)}
                data={ENGLISH_LEVEL_OPTIONS.map(({ value, label }) => ({ value, label }))}
                disabled={disabled}
                clearable
                size="md"
              />
            </SimpleGrid>
          </ProfileSection>
        </>
      )}
    </Stack>
  );
}

type ReviewProps = {
  values: CandidateFormValues;
  phone?: string;
  phoneVerified?: boolean;
};

export function CandidateReviewSummary({ values, phone, phoneVerified }: ReviewProps) {
  const education = educationSummaryFromForm(values) || educationLevelLabel(values.education_level) || "—";
  return (
    <Stack gap="sm">
      {phone ? (
        <Text size="sm">
          <strong>Phone:</strong> {phoneVerified ? "verified ✓ " : ""}
          {phone}
        </Text>
      ) : null}
      <Text size="sm"><strong>Name:</strong> {values.full_name || "—"}</Text>
      <Text size="sm"><strong>Email:</strong> {values.email || "—"}</Text>
      <Text size="sm"><strong>Title:</strong> {values.current_title || "—"}</Text>
      <Text size="sm"><strong>Cities:</strong> {values.cities.join(", ") || "—"}</Text>
      {values.gender ? (
        <Text size="sm">
          <strong>Gender:</strong> {GENDER_LABELS[values.gender as Gender] ?? values.gender}
        </Text>
      ) : null}
      <Text size="sm"><strong>Education:</strong> {education}</Text>
      {values.degree ? <Text size="sm"><strong>Degree:</strong> {degreeLabel(values.degree)}</Text> : null}
      <Text size="sm">
        <strong>Work status:</strong> {values.work_status === "fresher" ? "Fresher" : "Experienced"}
      </Text>
      {values.work_status === "working" && values.years_experience != null ? (
        <Text size="sm"><strong>Experience:</strong> {values.years_experience} years</Text>
      ) : null}
      <Text size="sm"><strong>Skills:</strong> {values.skills.join(", ") || "—"}</Text>
      <Text size="sm"><strong>Languages:</strong> {values.languages.join(", ") || "—"}</Text>
      {values.english_level ? (
        <Text size="sm">
          <strong>English:</strong>{" "}
          {ENGLISH_LEVEL_OPTIONS.find((option) => option.value === values.english_level)?.label}
        </Text>
      ) : null}
      <Text size="sm"><strong>Roles:</strong> {values.segments.join(", ") || "—"}</Text>
      {values.linkedin_url ? (
        <Text size="sm"><strong>LinkedIn:</strong> {values.linkedin_url}</Text>
      ) : null}
    </Stack>
  );
}
